import { useState, useEffect } from 'react';
import { DEFAULT_BRAND_COLOR } from '../constants/brand.js';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * BrandColorField — pick the brand's primary color (used on generated slides
 * & accents). Native picker, hex input, and a live preview swatch kept in sync.
 *
 * The hex text field is locally controlled so a half-typed value ("#25") can
 * show while typing without leaking a broken color to the parent. onChange
 * only fires with a complete, valid #rrggbb. The swatch background is inline
 * because it is user data, not a theme token.
 */
export function BrandColorField({ value, onChange }) {
  const [localHex, setLocalHex] = useState(value || DEFAULT_BRAND_COLOR);

  // Sync when the parent value changes (e.g. opening the editor on a project).
  useEffect(() => {
    if (value && value !== localHex) setLocalHex(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onText = (raw) => {
    const v = raw.startsWith('#') ? raw : `#${raw}`;
    setLocalHex(v);
    if (HEX_RE.test(v)) {
      onChange(v); // only propagate a complete, valid hex
    } else if (!raw.trim()) {
      // Field fully cleared — reset to the default so the parent + swatch agree
      // (otherwise the swatch shows the default while the form keeps the old color).
      setLocalHex(DEFAULT_BRAND_COLOR);
      onChange(DEFAULT_BRAND_COLOR);
    }
  };
  const onPick = (v) => { setLocalHex(v); onChange(v); };

  const safe = HEX_RE.test(localHex) ? localHex : DEFAULT_BRAND_COLOR;

  return (
    <div className="brand-color-field">
      <input
        type="color"
        className="brand-color-picker"
        value={safe}
        onChange={(e) => onPick(e.target.value)}
        aria-label="Brand color picker"
      />
      <input
        type="text"
        className="brand-color-hex mono"
        value={localHex}
        pattern="^#?[0-9a-fA-F]{6}$"
        spellCheck={false}
        maxLength={7}
        onChange={(e) => onText(e.target.value)}
        aria-label="Brand color hex"
      />
      <span className="brand-color-preview" style={{ background: safe }} aria-hidden="true" />
    </div>
  );
}
